import mixpanel from "mixpanel-browser";
import {
  useContext,
  createContext,
  useState,
  useRef,
  useEffect,
} from "react";

import { SCRIBENOTE_WS, WS_RECONNECT_WAIT } from "../constants";

const WSContext = createContext();

export const useWS = () => {
  return useContext(WSContext);
};

export function useProvideWS() {
  const [webSockets, setWebSockets] = useState({});
  const loading = useRef({});
  const attemptedReconnect = useRef(0);

  const setupWebSocket = (channel) => {
    const newWebSocket = new WebSocket(
      `${SCRIBENOTE_WS}/ws/${channel}`,
    );

    // If the connection fails, return an error
    if (!newWebSocket) {
      mixpanel.track("Websocket Error", {
        channel,
      });
      return;
    }

    return newWebSocket;
  };

  const connectWS = (channel, attemptReconnect = true) => {
    if (loading.current[channel]) {
      return null;
    }
    if (webSockets[channel]) {
      return webSockets[channel];
    }

    loading.current[channel] = true;
    const newWebSocket = setupWebSocket(channel);
    if (!newWebSocket) {
      loading.current[channel] = false;
      return;
    }

    const handleClose = () => {
      newWebSocket.close();
      newWebSocket.onclose = null;
      setWebSockets((prev) => ({
        ...prev,
        [channel]: null,
      }));
      if (attemptedReconnect.current < 5 && attemptReconnect) {
        attemptedReconnect.current += 1;
        let reconnectJitter = Math.random() * WS_RECONNECT_WAIT;
        let reconnectWait =
          attemptedReconnect.current ** 2 * WS_RECONNECT_WAIT;
        let reconnectTimeout = reconnectWait + reconnectJitter;
        setTimeout(() => {
          connectWS(channel);
        }, reconnectTimeout);
      }
    };

    newWebSocket.onopen = () => {
      setWebSockets((prev) => ({
        ...prev,
        [channel]: newWebSocket,
      }));
      attemptedReconnect.current = 0;
    };
    newWebSocket.onerror = handleClose;
    newWebSocket.onclose = handleClose;

    return newWebSocket;
  };

  useEffect(() => {
    Object.keys(webSockets).map(
      (channel) => (loading.current[channel] = false),
    );
  }, [webSockets]);

  return {
    connectWS,
    webSockets,
  };
}

export function WSProvider({ children }) {
  const webSockets = useProvideWS();
  return (
    <WSContext.Provider value={webSockets}>
      {children}
    </WSContext.Provider>
  );
}
